import { TypographyP2 } from '@heidi/design-system/typography';
import type { TranslationKey } from '@heidi/runtime/i18n';
import { useTranslations } from '@heidi/runtime/i18n';
import { FileText } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import {
  normalizeLibrarySearchQuery,
  normalizeLibrarySearchText,
  type LibraryItem,
} from '../../../contracts';
import { useDocumentsAndJobs } from '../../../data/library/user-documents/hooks/useDocumentsAndJobs';
import { SearchDropdownLayout } from '../hooks/useSearchDropdown';
import { useSearchDropdown } from '../hooks/useSearchDropdown.utils';

export interface SelectedExistingDoc {
  id: string;
  filename: string;
}

export interface DocumentSearchInputProps {
  /** Document IDs already added — excluded from dropdown results */
  excludedDocumentIds?: string[];
  /** Called when the user clicks a result in the dropdown */
  onSelectDocument: (doc: SelectedExistingDoc) => void;
  /** Maximum number of results to show in the dropdown. Default: 5 */
  maxResults?: number;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}

/**
 * Search input that queries the user's existing library documents.
 * Displays a dropdown of matching results filtered by filename.
 */
export const DocumentSearchInput = ({
  excludedDocumentIds = [],
  onSelectDocument,
  maxResults = 5,
  placeholder,
  autoFocus = false,
  className,
}: DocumentSearchInputProps) => {
  const { t } = useTranslations();
  const effectivePlaceholder =
    placeholder ?? t('library.wizard.documents.searchPlaceholder' as TranslationKey);

  const {
    query,
    setQuery,
    setIsOpen,
    containerRef,
    inputRef,
    showDropdown,
    clearAndClose,
  } = useSearchDropdown();

  const { items } = useDocumentsAndJobs();

  const excludedSet = useMemo(() => new Set(excludedDocumentIds), [excludedDocumentIds]);

  const filteredResults = useMemo(() => {
    const normalizedQuery = normalizeLibrarySearchQuery(query);
    return items
      .filter(
        (item: LibraryItem) =>
          item.type === 'document' &&
          item.status === 'completed' &&
          !excludedSet.has(item.id) &&
          (!normalizedQuery ||
            normalizeLibrarySearchText(item.filename).includes(normalizedQuery))
      )
      .slice(0, maxResults);
  }, [query, items, excludedSet, maxResults]);

  const handleSelect = useCallback(
    (doc: SelectedExistingDoc) => {
      onSelectDocument(doc);
      clearAndClose();
    },
    [onSelectDocument, clearAndClose]
  );

  return (
    <SearchDropdownLayout
      containerRef={containerRef}
      inputRef={inputRef}
      query={query}
      onQueryChange={setQuery}
      onOpen={() => setIsOpen(true)}
      placeholder={effectivePlaceholder}
      autoFocus={autoFocus}
      className={className}
      showDropdown={showDropdown && filteredResults.length > 0}
    >
      {filteredResults.map((doc) => (
        <button
          key={doc.id}
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            handleSelect({ id: doc.id, filename: doc.filename });
          }}
          className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors duration-150 hover:bg-surface-page"
        >
          <div className="flex size-6 shrink-0 items-center justify-center">
            <FileText size={16} className="text-text-secondary" />
          </div>
          <div className="min-w-0 flex-1">
            <TypographyP2 className="truncate">{doc.filename}</TypographyP2>
          </div>
        </button>
      ))}
    </SearchDropdownLayout>
  );
};
